/**
 * RitualTourOverlay
 *
 * 6-step guided tour overlay for the ritual detail page.
 */

import { useEffect, useRef, useState } from "react"
import { useTranslation } from "@/language"

interface TourStep {
  title: string
  description: string
  selector: string
  position: "left" | "right"
}

const CARD_WIDTH = 420
const CARD_HEIGHT = 185
const GAP = 24

interface RitualTourOverlayProps {
  currentStep: number
  onNext: () => void
  onBack: () => void
  onDone: () => void
  onSkip: () => void
}

export function RitualTourOverlay({ currentStep, onNext, onBack, onDone }: RitualTourOverlayProps) {
  const { t } = useTranslation("rituals")
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null)
  const highlightedRef = useRef<Element | null>(null)

  const ritualTourSteps: TourStep[] = [
    {
      title: t("tour.step1Title"),
      description: t("tour.step1Desc"),
      selector: "#tour-ritual-content",
      position: "right",
    },
    {
      title: t("tour.step2Title"),
      description: t("tour.step2Desc"),
      selector: "#tour-ritual-coach",
      position: "left",
    },
    {
      title: t("tour.step3Title"),
      description: t("tour.step3Desc"),
      selector: "#tour-ritual-action-card",
      position: "right",
    },
    {
      title: t("tour.step4Title"),
      description: t("tour.step4Desc"),
      selector: "#tour-ritual-start-btn",
      position: "right",
    },
    {
      title: t("tour.step5Title"),
      description: t("tour.step5Desc"),
      selector: "#tour-ritual-complete",
      position: "right",
    },
  ]

  const stepIndex = currentStep - 1
  const step = ritualTourSteps[stepIndex]
  const isFirst = currentStep === 1
  const isLast = currentStep === 5
  const totalSteps = 5

  useEffect(() => {
    const el = document.querySelector(step.selector) as HTMLElement | null
    if (!el) return

    const origPosition = el.style.position
    const origZIndex = el.style.zIndex
    const origPointerEvents = el.style.pointerEvents

    el.style.position = "relative"
    el.style.zIndex = "101"
    el.style.pointerEvents = "none"

    highlightedRef.current = el

    el.scrollIntoView({ behavior: "instant", block: "center" })

    const place = () => {
      const rect = el.getBoundingClientRect()
      const cardWidth = Math.min(CARD_WIDTH, window.innerWidth - 32)
      let top = 0
      let left = 0

      if (window.innerWidth < 640) {
        left = Math.max(16, (window.innerWidth - cardWidth) / 2)
        top = rect.bottom + GAP
      } else if (step.position === "right") {
        left = rect.right + GAP
        top = rect.top + rect.height / 2 - CARD_HEIGHT / 2
      } else {
        left = rect.left - cardWidth - GAP
        top = rect.top + rect.height / 2 - CARD_HEIGHT / 2
        if (left < 16) {
          left = rect.right + GAP
        }
      }

      if (window.innerWidth >= 640) {
        if (left < 16) left = 16
        if (left + cardWidth > window.innerWidth - 16) {
          left = Math.max(16, (window.innerWidth - cardWidth) / 2)
          top = rect.bottom + GAP
        }
      }
      if (top < 16) top = 16
      if (top + CARD_HEIGHT > window.innerHeight - 16) {
        top = Math.max(16, window.innerHeight - CARD_HEIGHT - 16)
      }

      setCardPos({ top, left })
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(place)
    })

    const onResize = () => place()
    window.addEventListener("resize", onResize)

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          place()
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      el.style.position = origPosition
      el.style.zIndex = origZIndex
      el.style.pointerEvents = origPointerEvents
      highlightedRef.current = null
      window.removeEventListener("resize", onResize)
      window.removeEventListener("scroll", onScroll)
    }
  }, [step.selector, step.position])

  if (!cardPos) {
    return (
      <div
        className="fixed inset-0 z-[100]"
        style={{ pointerEvents: "auto", backgroundColor: "rgba(34,34,34,0.9)" }}
      />
    )
  }

  return (
    <div className="fixed inset-0 z-[100]" style={{ pointerEvents: "auto" }}>
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(34,34,34,0.9)" }} />

      <div
        className="absolute max-w-[420px] w-[calc(100vw-32px)] rounded-[20px] flex flex-col gap-[15px]"
        style={{
          top: cardPos.top,
          left: cardPos.left,
          backgroundColor: "#eee2dc",
          border: "1px solid #f2f0eb",
          padding: "20px",
          pointerEvents: "auto",
        }}
      >
        <h3
          className="text-[18px] leading-[25px] font-semibold font-montserrat"
          style={{ color: "#020202" }}
        >
          {step.title}
        </h3>

        <p
          className="text-[14px] leading-[25px] font-normal font-montserrat"
          style={{ color: "#020202" }}
        >
          {step.description}
        </p>

        <div className="flex items-center justify-between h-10">
          <span
            className="text-[14px] leading-5 font-normal font-montserrat"
            style={{ color: "#020202", opacity: 0.5 }}
          >
            {currentStep} of {totalSteps}
          </span>

          <div className="flex items-center gap-[15px]">
            {!isFirst && (
              <button
                type="button"
                onClick={onBack}
                className="text-[14px] leading-5 font-semibold font-montserrat uppercase hover:opacity-70 transition-opacity cursor-pointer"
                style={{ color: "#020202" }}
              >
                {t("tour.back")}
              </button>
            )}
            <button
              type="button"
              onClick={isLast ? onDone : onNext}
              className="w-[120px] h-10 rounded-[100px] text-[14px] leading-5 font-semibold font-montserrat uppercase hover:opacity-90 transition-opacity flex items-center justify-center cursor-pointer"
              style={{ backgroundColor: "#020202", color: "#f2f0eb" }}
            >
              {isLast ? t("tour.done") : t("tour.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
